// ============================================================
// Pay Rocky — Route Guards
// ProtectedRoute: requires auth
// AdminRoute: requires admin role
// GuestRoute: redirects logged-in users away from auth pages
// ============================================================

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Spinner } from './ui/Primitives';

// ── Loading screen while auth state hydrates ─────────────────
function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--clr-bg)',
      flexDirection: 'column',
      gap: '16px',
      padding: '20px',
    }}>
      <Spinner size={28} />
      <p style={{
        color: 'var(--clr-text-2)',
        fontSize: '14px',
        textAlign: 'center',
        maxWidth: '300px',
      }}>
        Loading your account...
      </p>
    </div>
  );
}

// ── ProtectedRoute ────────────────────────────────────────────
// Redirects to /login if not authenticated.
// After login, redirects back to the originally requested URL.
export function ProtectedRoute({ children }) {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (
    currentUser?.role === 'user'
    && currentUser?.must_change_password
    && location.pathname !== '/dashboard'
  ) {
    return <Navigate to="/dashboard" state={{ from: location, forceProfile: true }} replace />;
  }

  return children;
}

// ── AdminRoute ────────────────────────────────────────────────
// Requires the user to be authenticated AND have admin role.
export function AdminRoute({ children }) {
  const { currentUser, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    // Logged-in users who aren't admin get sent to their dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// ── GuestRoute ────────────────────────────────────────────────
// Redirects already-authenticated users away from login/signup.
export function GuestRoute({ children }) {
  const { currentUser, loading, isAdmin, token } = useAuth();

  if (loading && token) return <LoadingScreen />;

  if (currentUser) {
    return <Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />;
  }

  return children;
}

export function PasswordRestrictedRoute({ children }) {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;

  if (currentUser?.role === 'user' && currentUser?.must_change_password) {
    return <Navigate to="/dashboard" state={{ from: location, forceProfile: true }} replace />;
  }

  return children;
}
